import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult
} from '@tanstack/react-query'
import wretch from 'wretch'
import { envConfig } from '../config'

/**
 * useQuery for queries gated behind `enabled` (typically an async MSAL token).
 *
 * react-query v5 redefined `isLoading` as `isPending && isFetching`, so a query
 * that is not enabled yet reports `isLoading: false` where v4 reported `true`.
 * Consumers doing `if (isLoading) return <Loading />` would otherwise render
 * their empty state while the token resolves. `isPending` in v5 means what
 * `isLoading` meant in v4, so re-expose it under the name consumers already use.
 */
export const useGatedQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> => {
  const result = useQuery(options)

  return { ...result, isLoading: result.isPending } as UseQueryResult<
    TData,
    TError
  >
}

/**
 * Query keys for the payment lists.
 *
 * The list keys on `[root, args]` while the mutations invalidate on `[root]`
 * alone, relying on react-query's prefix matching. Naming both here means the
 * relationship is stated once and cannot drift - and lets the tests assert on
 * the same values production uses, rather than on a copy that stays green when
 * a key is renamed.
 */
export const paymentsQueryKeys = {
  /** Invalidation root: matches every keyed variant beneath it. */
  lists: () => ['payments-list'] as const,
  /** A single filtered list. */
  list: (args: unknown) => ['payments-list', args] as const
}

/**
 * Retry rule shared by the query hooks: give up immediately on a 404, retry
 * anything else up to twice.
 *
 * A 404 means the resource genuinely is not there, so retrying only delays the
 * error by two round trips. Everything else - 5xx, a dropped connection, an
 * error with no status at all - is potentially transient.
 *
 * This was copy-pasted verbatim into thirteen query hooks across five files
 * before being lifted here. Two call sites in usePayment.ts keep an inline
 * copy on purpose - each carries a comment explaining why a 404 is expected
 * for that particular query, which folding them in would delete.
 */
export const shouldRetryQuery = (attempts: number, error: unknown): boolean => {
  const status = (error as { status?: number })?.status
  return status !== 404 && attempts < 2
}

export const documentService = (endPoint: string, token: string) => {
  const { documentService: docServiceConfig } = envConfig()

  return wretch(`${docServiceConfig.url}/${endPoint}`)
    .headers({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    })
    .auth(token ? `Bearer ${token}` : '')
}

export const paymentService = (
  endPoint: string,
  token: string,
  options?: { acceptType?: string }
) => {
  const { apiUrl } = envConfig()

  // Backward-compatible mapping: allow symbolic values 'csv' | 'json' or full MIME strings
  const requested = options?.acceptType
  const acceptHeader = !requested
    ? 'application/json'
    : requested === 'csv'
    ? 'text/csv'
    : requested === 'json'
    ? 'application/json'
    : requested // assume caller provided a valid MIME type

  return (
    wretch(`${apiUrl}/${endPoint}`)
      // Simple headers to avoid CORS issues
      .headers({
        'Content-Type': 'application/json',
        Accept: acceptHeader
      })
      // Only add auth header if we have a meaningful token
      .auth(
        token && token !== 'somethingNotUserSpecific' ? `Bearer ${token}` : ''
      )
  )
}
